import React, { JSX, useEffect, useState } from 'react';
import { Flex, Pagination, Spin } from 'antd';
import classnames from 'classnames';
import { Comment } from './Comment';
import { CommentInput } from './CommentInput';

import { CommentItem } from './types';

import cn from './styles.module.scss';

export { CommentIconButton } from './CommentIconButton';
export type { CommentIconButtonProps } from './CommentIconButton';

const PAGE_SIZE = 10;
const DEFAULT_EMPTY_TEXT = 'There are no comments yet';

export type CommentsClassNames = {
  list?: string;
  item?: string;
  input?: string;
  pagination?: string;
  empty?: string;
};

type CommonCommentProps = {
  className?: string;
  classNames?: CommentsClassNames;
  emptyText?: React.ReactNode;
  showPagination?: 'always' | 'onDemand';
  onRefresh: (page: number, pageSize: number) => Promise<CommentItem[] | undefined>;
  initialCommentsCount: number;
};

export type EditableCommentProps = CommonCommentProps & {
  isReadonly?: false;
  onSubmit: (commentText: string) => Promise<void>;
  onUpdate: (id: number, commentText: string) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  checkEditAvailable: (userId: number) => boolean;
  checkDeleteAvailable: (userId: number) => boolean;
};

export type ReadOnlyCommentProps = CommonCommentProps & {
  isReadonly: true;
};

export type CommentProps = EditableCommentProps | ReadOnlyCommentProps;

export const Comments = ({
  showPagination = 'always',
  onRefresh,
  initialCommentsCount,
  className,
  classNames,
  emptyText = DEFAULT_EMPTY_TEXT,
  ...props
}: CommentProps): JSX.Element => {
  const [isLoading, setIsLoading] = useState(false);
  const [commentsCount, setCommentsCount] = useState(initialCommentsCount);
  const [comments, setComments] = useState<CommentItem[]>([]);
  const [page, setPage] = useState(1);

  const isPaginationVisible = showPagination === 'always' || (commentsCount > PAGE_SIZE);

  const handleRefresh = async (loadPage = page) => {
    try {
      setIsLoading(true);
      let loadedComments = await onRefresh(loadPage, PAGE_SIZE);
      if (loadedComments) {
        if (loadedComments.length === 0 && page > 1) {
          setPage(loadPage - 1);
          loadedComments = await onRefresh(loadPage - 1, PAGE_SIZE);
        }
        setComments(loadedComments || []);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (commentText: string) => {
    if (props.isReadonly) {
      return;
    }
    try {
      setIsLoading(true);
      await props.onSubmit(commentText);
      setPage(1);
      setCommentsCount(commentsCount + 1);
      await handleRefresh(1);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async (commentId: number, text: string) => {
    if (props.isReadonly) {
      return;
    }
    try {
      setIsLoading(true);
      await props.onUpdate(commentId, text);
      await handleRefresh();
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (commentId: number) => {
    if (props.isReadonly) {
      return;
    }
    try {
      setIsLoading(true);
      await props.onDelete(commentId);
      setCommentsCount(commentsCount - 1);
      await handleRefresh();
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = async (newPage: number) => {
    setPage(newPage);
    await handleRefresh(newPage);
  };

  useEffect(() => {
    handleRefresh();
  }, []);

  useEffect(() => {
    setCommentsCount(initialCommentsCount);
  }, [initialCommentsCount]);

  return (
    <Flex vertical className={classnames(cn.root, className)}>
      <div className={classnames(cn.list, classNames?.list)}>
        {comments.length === 0 && (
          <div className={classnames(cn.empty, classNames?.empty)}>
            {emptyText}
          </div>
        )}

        {comments.map((comment) => (
          <Comment
            key={comment.id}
            comment={comment}
            isLoading={isLoading}
            onDelete={handleDelete}
            onUpdate={handleUpdate}
            isEditAvailable={props.isReadonly ? false : props.checkEditAvailable(comment.user?.id || 0)}
            isDeleteAvailable={props.isReadonly ? false : props.checkDeleteAvailable(comment.user?.id || 0)}
            className={classNames?.item}
          />
        ))}
      </div>

      {isPaginationVisible && (
        <div className={classnames(cn.pagination, classNames?.pagination)}>
          <Pagination
            size="small"
            current={page}
            total={commentsCount}
            onChange={handlePageChange}
            pageSize={PAGE_SIZE}
            disabled={isLoading}
          />
        </div>
      )}

      {!props.isReadonly && (
        <div className={cn.inputWrapper}>
          <CommentInput
            className={classnames(cn.input, classNames?.input)}
            onSubmit={handleSubmit}
            isLoading={isLoading}
          />
          {isLoading && (
            <div className={cn.loader}>
              <Spin />
            </div>
          )}
        </div>
      )}
    </Flex>
  );
};
